/**
 * Metpure User Manuals Controller
 * Handles live client-side search, SKU matching, category navigation, view mode toggle,
 * keyword highlighting, and deep linking
 */
class MetpureManuals {
  constructor(container) {
    this.container = container;
    this.searchInput = container.querySelector('.manuals-metpure__search-input');
    this.searchClearBtn = container.querySelector('.manuals-metpure__search-clear');
    this.searchStatus = container.querySelector('.manuals-metpure__search-status');
    this.navButtons = container.querySelectorAll('.manuals-metpure__nav-btn');
    this.groups = container.querySelectorAll('.manuals-metpure__group');
    this.cards = container.querySelectorAll('.manuals-metpure__card');
    this.itemsWrappers = container.querySelectorAll('.manuals-metpure__items');
    this.emptyState = container.querySelector('.manuals-metpure__empty');
    this.emptyResetBtn = container.querySelector('.manuals-metpure__empty-reset');
    this.showingText = container.querySelector('.manuals-metpure__showing-text');
    this.viewButtons = container.querySelectorAll('.manuals-metpure__view-btn');

    this.currentCategory = 'all';
    this.currentQuery = '';
    this.currentView = localStorage.getItem('metpure_manuals_view') || 'grid';

    // Cache original inner HTML for keyword highlighting
    this.cardOriginals = new Map();
    this.cards.forEach((card) => {
      const titleEl = card.querySelector('.manuals-metpure__card-title');
      const descEl = card.querySelector('.manuals-metpure__card-desc');
      const skuEl = card.querySelector('.manuals-metpure__sku-badge');
      this.cardOriginals.set(card, {
        title: titleEl ? titleEl.innerHTML : '',
        desc: descEl ? descEl.innerHTML : '',
        sku: skuEl ? skuEl.innerHTML : ''
      });
    });

    this.init();
  }

  init() {
    this.applyViewMode(this.currentView);
    this.bindEvents();
    this.updateCounts();
    this.handleDeepLink();
  }

  bindEvents() {
    // Category pill filter buttons
    this.navButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const category = btn.dataset.category;
        this.setCategory(category);
      });
    });

    // Search input handler
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => {
        this.handleSearch(e.target.value);
      });

      if (this.searchClearBtn) {
        this.searchClearBtn.addEventListener('click', () => {
          this.searchInput.value = '';
          this.handleSearch('');
          this.searchInput.focus();
        });
      }
    }

    // View mode switchers (Grid vs List)
    this.viewButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.view;
        this.applyViewMode(view);
      });
    });

    // Reset button in empty state
    if (this.emptyResetBtn) {
      this.emptyResetBtn.addEventListener('click', () => {
        if (this.searchInput) this.searchInput.value = '';
        this.setCategory('all');
        this.handleSearch('');
      });
    }

    // Direct download handler for PDF files
    const downloadBtns = this.container.querySelectorAll('.js-manual-download');
    downloadBtns.forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const url = btn.dataset.downloadUrl || btn.getAttribute('href');
        const filename = btn.dataset.filename || 'manual.pdf';
        if (!url || url === '#') return;

        // Force direct file download
        e.preventDefault();
        const textSpan = btn.querySelector('span');
        const originalText = textSpan ? textSpan.textContent : 'Download Manual';
        if (textSpan) textSpan.textContent = 'Downloading...';

        try {
          const response = await fetch(url, { mode: 'cors' });
          if (!response.ok) throw new Error('Fetch failed');
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
          }, 200);
        } catch (err) {
          // Fallback: use temporary download anchor
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.setAttribute('download', filename);
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
          }, 200);
        } finally {
          setTimeout(() => {
            if (textSpan) textSpan.textContent = originalText;
          }, 800);
        }
      });
    });
  }

  applyViewMode(view) {
    this.currentView = view;
    try {
      localStorage.setItem('metpure_manuals_view', view);
    } catch (e) {
      // Storage unavailable or disabled
    }

    this.viewButtons.forEach((btn) => {
      if (btn.dataset.view === view) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });

    this.itemsWrappers.forEach((wrapper) => {
      if (view === 'list') {
        wrapper.classList.add('is-list-view');
      } else {
        wrapper.classList.remove('is-list-view');
      }
    });
  }

  setCategory(category) {
    this.currentCategory = category;

    this.navButtons.forEach((btn) => {
      if (btn.dataset.category === category) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });

    this.filterCards();
  }

  handleSearch(query) {
    this.currentQuery = query.trim().toLowerCase();

    if (this.searchClearBtn) {
      this.searchClearBtn.style.display = this.currentQuery.length > 0 ? 'inline-flex' : 'none';
    }

    this.filterCards();
  }

  filterCards() {
    let matchCount = 0;

    this.cards.forEach((card) => {
      const cardCategory = card.dataset.category || '';
      const original = this.cardOriginals.get(card) || {};

      const titleEl = card.querySelector('.manuals-metpure__card-title');
      const descEl = card.querySelector('.manuals-metpure__card-desc');
      const skuEl = card.querySelector('.manuals-metpure__sku-badge');

      // Reset content to original before checking matches
      if (titleEl && original.title) titleEl.innerHTML = original.title;
      if (descEl && original.desc) descEl.innerHTML = original.desc;
      if (skuEl && original.sku) skuEl.innerHTML = original.sku;

      const titleText = (titleEl ? titleEl.textContent : '').toLowerCase();
      const descText = (descEl ? descEl.textContent : '').toLowerCase();
      const skuText = (skuEl ? skuEl.textContent : '').toLowerCase();

      const matchesCategory =
        this.currentCategory === 'all' || cardCategory === this.currentCategory;

      const matchesSearch =
        !this.currentQuery ||
        titleText.includes(this.currentQuery) ||
        descText.includes(this.currentQuery) ||
        skuText.includes(this.currentQuery);

      if (matchesCategory && matchesSearch) {
        card.classList.remove('is-hidden');
        matchCount++;

        // Apply keyword highlighting if query exists
        if (this.currentQuery.length > 1) {
          this.highlightText(titleEl, this.currentQuery);
          this.highlightText(descEl, this.currentQuery);
          this.highlightText(skuEl, this.currentQuery);
        }
      } else {
        card.classList.add('is-hidden');
      }
    });

    // Group containers visibility
    this.groups.forEach((group) => {
      const groupCategory = group.dataset.category;
      const visibleCards = group.querySelectorAll('.manuals-metpure__card:not(.is-hidden)');

      if (
        (this.currentCategory === 'all' || groupCategory === this.currentCategory) &&
        visibleCards.length > 0
      ) {
        group.classList.remove('is-hidden');
      } else {
        group.classList.add('is-hidden');
      }
    });

    // Empty state handling
    if (this.emptyState) {
      if (matchCount === 0) {
        this.emptyState.classList.add('is-visible');
      } else {
        this.emptyState.classList.remove('is-visible');
      }
    }

    // Search status text
    if (this.searchStatus) {
      if (this.currentQuery.length > 0) {
        this.searchStatus.textContent = `Found ${matchCount} manual${matchCount === 1 ? '' : 's'} matching "${this.currentQuery}"`;
      } else {
        this.searchStatus.textContent = '';
      }
    }

    // Showing text
    if (this.showingText) {
      this.showingText.textContent = `Showing ${matchCount} of ${this.cards.length} manuals`;
    }
  }

  highlightText(el, query) {
    if (!el) return;
    const innerHTML = el.innerHTML;
    // Safe escaping for regex special chars
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    el.innerHTML = innerHTML.replace(
      regex,
      '<mark class="manuals-metpure__highlight">$1</mark>'
    );
  }

  updateCounts() {
    this.navButtons.forEach((btn) => {
      const cat = btn.dataset.category;
      const countEl = btn.querySelector('.manuals-metpure__nav-count');
      if (!countEl) return;

      if (cat === 'all') {
        countEl.textContent = this.cards.length;
      } else {
        const count = this.container.querySelectorAll(
          `.manuals-metpure__card[data-category="${cat}"]`
        ).length;
        countEl.textContent = count;
      }
    });

    if (this.showingText) {
      this.showingText.textContent = `Showing all ${this.cards.length} manuals`;
    }
  }

  handleDeepLink() {
    const hash = window.location.hash;
    if (!hash) return;

    try {
      const targetCard = this.container.querySelector(hash);
      if (targetCard && targetCard.classList.contains('manuals-metpure__card')) {
        this.setCategory('all');
        targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        targetCard.classList.add('is-featured');
      }
    } catch (e) {
      // Ignore invalid hash
    }
  }
}

// Auto-initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const manualSections = document.querySelectorAll('.manuals-metpure');
  manualSections.forEach((section) => {
    new MetpureManuals(section);
  });
});

// Shopify Theme Editor Section Lifecycle Handlers
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.manuals-metpure');
    if (section) {
      new MetpureManuals(section);
    }
  });
}

